import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import axios from "axios";
import toast from "react-hot-toast";
import {
  ArrowLeft,
  Banknote,
  Check,
  CheckCircle2,
  CreditCard,
  Info,
  ShieldCheck,
  Smartphone,
  UploadCloud,
  WalletCards,
} from "lucide-react";
import { useAuth } from "../../auth/context/AuthContext";

export default function PayNow() {
  const { user, refreshUser } = useAuth();
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState([]);
  const [method, setMethod] = useState("manual");
  const [screenshot, setScreenshot] = useState(null);
  const [paymentSettings, setPaymentSettings] = useState({
    manual_mobile_money_number: "",
    hubtel_enabled: false,
    tier_discount_percent: 0,
    loyalty_tier: "bronze",
  });
  const [loading, setLoading] = useState(false);
  const [paymentStep, setPaymentStep] = useState(1);

  const loadItems = () =>
    axios
      .get("/api/customer/items", { params: { payment_status: "unpaid" } })
      .then((r) => setItems(r.data))
      .catch(() => {});

  useEffect(() => {
    // Refresh user so tier discount reflects current tier
    refreshUser();
    loadItems();
    axios
      .get("/api/customer/payments/settings")
      .then((r) =>
        setPaymentSettings({
          manual_mobile_money_number: r.data.manual_mobile_money_number || "",
          hubtel_enabled: Boolean(r.data.hubtel_enabled),
          tier_discount_percent: Number(r.data.tier_discount_percent || 0),
          loyalty_tier: r.data.loyalty_tier || "bronze",
        }),
      )
      .catch(() => {});
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!paymentSettings.hubtel_enabled && method === "hubtel") {
      setMethod("manual");
    }
  }, [paymentSettings.hubtel_enabled, method]);

  const toggle = (id) =>
    setSelected((current) =>
      current.includes(id)
        ? current.filter((value) => value !== id)
        : [...current, id],
    );

  const tier = paymentSettings.loyalty_tier || user?.loyalty_tier || "bronze";
  const tierDiscountPct = Number(paymentSettings.tier_discount_percent || 0);

  const subtotal = items
    .filter((item) => selected.includes(item.id))
    .reduce((sum, item) => sum + parseFloat(item.final_fee || 0), 0);

  const discountAmount = subtotal * (tierDiscountPct / 100);
  const total = subtotal - discountAmount;

  const totalUsd = items
    .filter((item) => selected.includes(item.id))
    .reduce((sum, item) => sum + parseFloat(item.final_fee_usd || 0), 0);

  const tierBadgeLabel = useMemo(() => `${tier} discount`, [tier]);

  const onDrop = useCallback((files) => {
    if (files[0]) setScreenshot(files[0]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [] },
    maxFiles: 1,
  });

  const handleSubmit = async () => {
    if (!selected.length) return toast.error("Select at least one item");

    setLoading(true);
    try {
      if (method === "hubtel") {
        if (!paymentSettings.hubtel_enabled) {
          return toast.error(
            "Hubtel direct payment is not available right now",
          );
        }
        const res = await axios.post("/api/customer/payments/hubtel", {
          item_ids: selected,
        });
        toast.success("Redirecting to Hubtel...");
        window.open(res.data.checkoutUrl, "_blank");
      } else {
        if (!screenshot) return toast.error("Please upload payment screenshot");

        const formData = new FormData();
        formData.append("screenshot", screenshot);
        formData.append("item_ids", JSON.stringify(selected));
        await axios.post("/api/customer/payments/manual", formData);

        toast.success("Payment submitted! Admin will verify shortly.");
        setSelected([]);
        setScreenshot(null);
        await loadItems();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Payment failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="payment-page" data-payment-step={paymentStep}>
      <div className="page-header">
        <Link to="/home" className="back-btn">
          ←
        </Link>
        <div>
          <p className="payment-eyebrow">PAYMENT CENTER</p>
          <h1>Pay your shipping fees</h1>
          <p className="payment-header-copy">
            Choose your shipment, select a payment method, and we will take care
            of the rest.
          </p>
        </div>
      </div>

      <div className="page-content payment-content">
        {items.length === 0 ? (
          <div className="empty-state payment-empty-state">
            <div className="icon">
              <CheckCircle2 size={34} />
            </div>
            <p style={{ fontWeight: 600 }}>No unpaid items</p>
            <p>All your items are paid up.</p>
          </div>
        ) : (
          <>
            <section
              className="payment-guide"
              aria-labelledby="payment-guide-title"
            >
              <div className="payment-guide-icon">
                <CreditCard size={24} />
              </div>
              <div className="payment-guide-copy">
                <p className="payment-eyebrow">A SIMPLE THREE-STEP PAYMENT</p>
                <h2 id="payment-guide-title">
                  Let&apos;s clear your shipment balance
                </h2>
                <p>
                  Only the items you select will be included in this payment.
                  You can pay one item or several items together.
                </p>
              </div>
              <div className="payment-steps">
                <div
                  className={`payment-step${paymentStep >= 1 ? " payment-step-active" : ""}`}
                >
                  <span>1</span>
                  <strong>Select items</strong>
                </div>
                <div
                  className={`payment-step${paymentStep >= 2 ? " payment-step-active" : ""}`}
                >
                  <span>2</span>
                  <strong>Choose how to pay</strong>
                </div>
                <div
                  className={`payment-step${paymentStep >= 3 ? " payment-step-active" : ""}`}
                >
                  <span>3</span>
                  <strong>Confirm payment</strong>
                </div>
              </div>
            </section>
            <div className="payment-step-panel payment-step-panel-1 payment-section-heading">
              <div>
                <p className="payment-step-kicker">STEP 1</p>
                <h2>Select the shipment items to pay</h2>
              </div>
              <span className="payment-selection-count">
                {selected.length} selected
              </span>
            </div>
            <p className="payment-step-panel payment-step-panel-1 payment-helper">
              <Info size={15} /> Tap any item to select or remove it from this
              payment.
            </p>
            <div className="payment-step-panel payment-step-panel-1 payment-items-list">
              {items.map((item) => {
                const isSea = item.transport_mode === "sea";
                const billedBy = isSea
                  ? `${parseFloat(item.volume_cbm || 0).toFixed(4)} CBM`
                  : `${parseFloat(item.weight_kg || 0).toFixed(2)} kg`;
                const modeLabel = isSea ? "Sea" : "Air";
                const isSelected = selected.includes(item.id);

                return (
                  <div
                    key={item.id}
                    className={`card payment-item-card${isSelected ? " payment-item-card-selected" : ""}`}
                    onClick={() => toggle(item.id)}
                    style={{
                      cursor: "pointer",
                      border: `1.5px solid ${isSelected ? "var(--brand-blue)" : "var(--line)"}`,
                      background: isSelected ? "var(--accent-soft)" : "white",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 10,
                      }}
                    >
                      <div
                        style={{
                          width: 20,
                          height: 20,
                          borderRadius: 4,
                          border: `2px solid ${isSelected ? "var(--brand-blue)" : "var(--line-strong)"}`,
                          background: isSelected
                            ? "var(--brand-blue)"
                            : "white",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          marginTop: 2,
                        }}
                      >
                        {isSelected && (
                          <span style={{ color: "white", fontSize: 12 }}>
                            ✓
                          </span>
                        )}
                      </div>

                      <div style={{ flex: 1 }}>
                        <p
                          style={{
                            fontWeight: 600,
                            fontSize: 14,
                            marginBottom: 3,
                          }}
                        >
                          {item.goods_name}
                        </p>

                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            marginBottom: 4,
                          }}
                        >
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 700,
                              padding: "2px 7px",
                              borderRadius: 20,
                              background: isSea ? "#e0f2fe" : "#f0fdf4",
                              color: isSea ? "#0369a1" : "#15803d",
                            }}
                          >
                            {modeLabel}
                          </span>
                          <span style={{ fontSize: 12, color: "#6b7280" }}>
                            Billed by: <strong>{billedBy}</strong>
                          </span>
                        </div>

                        <div
                          style={{
                            background: "#f8fafc",
                            borderRadius: 8,
                            padding: "6px 10px",
                          }}
                        >
                          {item.final_fee_usd &&
                            parseFloat(item.final_fee_usd) > 0 && (
                              <p style={{ fontSize: 11, color: "#6b7280" }}>
                                USD ${parseFloat(item.final_fee_usd).toFixed(2)}
                                {item.exchange_rate_used && (
                                  <span style={{ color: "#9ca3af" }}>
                                    {" "}
                                    x {item.exchange_rate_used} GHS/USD
                                  </span>
                                )}
                              </p>
                            )}
                          {item.discount_applied > 0 && (
                            <p
                              style={{
                                fontSize: 11,
                                color: "#9ca3af",
                                textDecoration: "line-through",
                              }}
                            >
                              GHS {parseFloat(item.shipping_fee).toFixed(2)}
                            </p>
                          )}
                          <p
                            style={{
                              fontSize: 15,
                              fontWeight: 800,
                              color: "var(--brand-blue)",
                            }}
                          >
                            GHS {parseFloat(item.final_fee).toFixed(2)}
                          </p>
                          {item.discount_applied > 0 && (
                            <p style={{ fontSize: 10, color: "#16a34a" }}>
                              -{item.discount_applied}% discount
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="payment-step-panel payment-step-panel-1 payment-wizard-actions">
              <button
                type="button"
                className="btn btn-primary payment-next-button"
                onClick={() => {
                  if (!selected.length)
                    return toast.error("Select at least one item");
                  setPaymentStep(2);
                }}
              >
                Review amount <span aria-hidden="true">-&gt;</span>
              </button>
            </div>

            {selected.length > 0 && (
              <div
                className="card payment-step-panel payment-step-panel-2 payment-summary-card"
                style={{
                  background: "var(--brand-blue)",
                  color: "white",
                  marginTop: 8,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                  }}
                >
                  <div>
                    <p style={{ fontWeight: 600 }}>
                      {selected.length} item(s) selected
                    </p>
                    {totalUsd > 0 && (
                      <p style={{ fontSize: 12, opacity: 0.8 }}>
                        Approx. USD ${totalUsd.toFixed(2)}
                      </p>
                    )}
                    {tierDiscountPct > 0 && (
                      <p style={{ fontSize: 12, opacity: 0.75, marginTop: 2 }}>
                        Subtotal: GHS {subtotal.toFixed(2)}
                      </p>
                    )}
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <p style={{ fontWeight: 800, fontSize: 20 }}>
                      GHS {total.toFixed(2)}
                    </p>
                    {tierDiscountPct > 0 && (
                      <p
                        style={{
                          fontSize: 11,
                          background: "rgba(255,255,255,0.2)",
                          borderRadius: 20,
                          padding: "2px 8px",
                          marginTop: 4,
                        }}
                      >
                        -{tierDiscountPct}% {tierBadgeLabel}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="payment-step-panel payment-step-panel-2 payment-section-heading payment-method-heading">
              <div>
                <p className="payment-step-kicker">STEP 2</p>
                <h2>Choose how you want to pay</h2>
              </div>
            </div>
            <div className="payment-step-panel payment-step-panel-2 payment-methods">
              {[
                {
                  key: "manual",
                  label: "Mobile Money / Bank",
                  desc: "Upload screenshot for verification",
                },
                {
                  key: "hubtel",
                  label: "Hubtel (Direct Pay)",
                  desc: paymentSettings.hubtel_enabled
                    ? "Instant online payment"
                    : "Currently unavailable",
                  disabled: !paymentSettings.hubtel_enabled,
                },
              ].map((option) => (
                <div
                  key={option.key}
                  onClick={() => {
                    if (option.disabled) return;
                    setMethod(option.key);
                  }}
                  className={`card payment-method-card${method === option.key ? " payment-method-card-selected" : ""}`}
                  style={{
                    flex: 1,
                    cursor: option.disabled ? "not-allowed" : "pointer",
                    opacity: option.disabled ? 0.6 : 1,
                    border: `1.5px solid ${method === option.key ? "var(--brand-blue)" : "var(--line)"}`,
                    background:
                      method === option.key ? "var(--accent-soft)" : "white",
                    padding: 12,
                  }}
                >
                  <p style={{ fontSize: 13, fontWeight: 600 }}>
                    {option.label}
                  </p>
                  <p style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>
                    {option.desc}
                  </p>
                </div>
              ))}
            </div>

            <div className="payment-step-panel payment-step-panel-2 payment-wizard-actions payment-wizard-actions-split">
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => setPaymentStep(1)}
              >
                Back
              </button>
              <button
                type="button"
                className="btn btn-primary payment-next-button"
                onClick={() => setPaymentStep(3)}
              >
                Continue to payment <span aria-hidden="true">-&gt;</span>
              </button>
            </div>

            {method === "manual" && (
              <>
                <div className="payment-step-panel payment-step-panel-3 payment-section-heading payment-method-heading">
                  <div>
                    <p className="payment-step-kicker">STEP 3</p>
                    <h2>Send payment and confirm it</h2>
                  </div>
                </div>
                <div className="payment-step-panel payment-step-panel-3 payment-wizard-actions payment-wizard-actions-split payment-stage-back">
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={() => setPaymentStep(2)}
                  >
                    Back to payment method
                  </button>
                </div>
                <div className="payment-step-panel payment-step-panel-3 payment-account-card">
                  <div className="payment-account-icon">
                    <Smartphone size={20} />
                  </div>
                  <div className="payment-account-copy">
                    <p className="payment-account-label">
                      SEND YOUR PAYMENT TO THIS NUMBER
                    </p>
                    <p className="payment-account-number">
                      {paymentSettings.manual_mobile_money_number ||
                        "Not configured yet"}
                    </p>
                    <p className="payment-account-note">
                      Mobile Money payment destination. Check the number before
                      confirming your transfer.
                    </p>
                  </div>
                  <ShieldCheck size={20} className="payment-secure-icon" />
                </div>
                <div className="payment-step-panel payment-step-panel-3 payment-instruction-row">
                  <span className="payment-instruction-icon">
                    <Banknote size={16} />
                  </span>
                  <p>
                    Pay the total shown above, then upload a clear screenshot of
                    the successful transaction.
                  </p>
                </div>

                <div
                  {...getRootProps()}
                  className={`payment-step-panel payment-step-panel-3 dropzone payment-proof-dropzone ${isDragActive ? "active" : ""}`}
                >
                  <input {...getInputProps()} />
                  {screenshot ? (
                    <div>
                      <CheckCircle2
                        size={28}
                        className="payment-upload-success"
                      />
                      <p
                        style={{
                          fontSize: 14,
                          fontWeight: 600,
                          color: "#15803d",
                        }}
                      >
                        Screenshot ready
                      </p>
                      <p style={{ fontSize: 12, color: "#6b7280" }}>
                        {screenshot.name} · Tap to change
                      </p>
                    </div>
                  ) : (
                    <div>
                      <UploadCloud size={28} className="payment-upload-icon" />
                      <p className="payment-upload-title">
                        Upload your payment screenshot
                      </p>
                      <p>
                        Drag and drop it here, or <strong>tap to choose</strong>
                      </p>
                    </div>
                  )}
                </div>
              </>
            )}

            {method === "hubtel" && (
              <div className="payment-step-panel payment-step-panel-3 payment-instruction-row payment-hubtel-note">
                <span className="payment-instruction-icon">
                  <WalletCards size={16} />
                </span>
                <p>
                  You will be securely redirected to Hubtel to complete the
                  payment.
                </p>
              </div>
            )}

            <button
              className="payment-step-panel payment-step-panel-3 btn btn-primary btn-full"
              style={{ marginTop: 16 }}
              onClick={handleSubmit}
              disabled={loading || !selected.length}
            >
              {loading ? (
                <span className="spinner" />
              ) : method === "hubtel" ? (
                "Continue to Hubtel"
              ) : (
                "Submit payment for verification"
              )}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
